import { Box, Button, HStack, Image, Text } from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { ReactNode, useEffect, useState } from "react";
import { normalizeTokenImageUrl } from "../../helpers/tokenImage";

export interface TokenPillProps {
  image: string;
  symbol: string;
  /** When set, renders as a menu/dropdown trigger. */
  onClick?: () => void;
  /** Custom trigger (e.g. Chakra MenuButton). Takes precedence over onClick. */
  trigger?: ReactNode;
  showChevron?: boolean;
}

export const TokenPill = ({
  image,
  symbol,
  onClick,
  trigger,
  showChevron = false,
}: TokenPillProps) => {
  const [imgSrc, setImgSrc] = useState(() => normalizeTokenImageUrl(image));

  useEffect(() => {
    setImgSrc(normalizeTokenImageUrl(image));
  }, [image]);

  const content = (
    <HStack spacing={2} minW={0}>
      <Image
        src={imgSrc}
        boxSize="24px"
        borderRadius="full"
        flexShrink={0}
        onError={() => setImgSrc(normalizeTokenImageUrl("/unknown.svg"))}
      />
      <Text fontWeight="semibold" fontSize="md" color="white" noOfLines={1}>
        {symbol}
      </Text>
      {showChevron && (
        <ChevronDownIcon boxSize={4} color="whiteAlpha.800" flexShrink={0} />
      )}
    </HStack>
  );

  const pillSx = {
    bg: "whiteAlpha.100",
    borderRadius: "full",
    px: 3,
    py: 2,
    height: "auto",
    minH: "40px",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    _hover: onClick || trigger ? { bg: "whiteAlpha.200" } : undefined,
  };

  if (trigger) {
    return <Box sx={pillSx}>{trigger}</Box>;
  }

  if (onClick) {
    return (
      <Button
        type="button"
        variant="unstyled"
        onClick={onClick}
        sx={pillSx}
        display="flex"
        alignItems="center"
      >
        {content}
      </Button>
    );
  }

  return (
    <Box sx={pillSx} display="inline-flex" alignItems="center">
      {content}
    </Box>
  );
};
